import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiCopy, FiCheck, FiExternalLink } from 'react-icons/fi';

/**
 * Side drawer for a single guest feedback record. Mirrors the visual
 * vocabulary of `ServiceRequestDrawer` (zinc neutrals, pill badges, soft
 * shadows) but is read-only: the survey itself is anonymous + immutable so
 * there's nothing to edit, only to read and copy.
 *
 * Q4 (`openComment`) is the share-worthy artifact, hence the explicit
 * "Copy quote" button — we expect ops to paste these into Slack/Linear.
 */
const STAR_FILL = '#F4B400';
const STAR_EMPTY = '#E5E7EB';

const StarRow = ({ value }) => {
  const safe = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${safe} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={n <= safe ? STAR_FILL : 'none'}
          stroke={n <= safe ? STAR_FILL : STAR_EMPTY}
          strokeWidth="1.5"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </div>
  );
};

// Human-readable mappings for the enum values stored on the backend. Keep
// the keys in sync with `backend/src/models/GuestFeedback.js` — a missing
// label here just falls back to the raw enum string, which is acceptable
// degradation.
const ARRIVAL_EXPERIENCE_LABELS = {
  loved_it: 'Loved it',
  helpful: 'Helpful',
  okay: 'Okay',
  could_be_better: 'Could be better',
};

const ARRIVAL_CONDITION_LABELS = {
  great: 'Great',
  small_issues: 'Small issues',
  missing_or_incorrect: 'Missing or incorrect',
  not_checked_yet: "Hadn't checked yet",
};

const BIGGEST_DIFFERENCE_LABELS = {
  saved_grocery_trip: 'Saved a grocery trip',
  easier_arrival: 'Easier arrival',
  more_prepared: 'More prepared',
  more_thoughtful: 'More thoughtful',
  more_premium: 'Felt more premium',
  other: 'Something else',
};

const Badge = ({ children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'bg-zinc-100 text-zinc-700',
    positive: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
};

const arrivalExperienceTone = (val) => {
  if (val === 'loved_it') return 'positive';
  if (val === 'helpful') return 'positive';
  if (val === 'okay') return 'neutral';
  if (val === 'could_be_better') return 'warning';
  return 'neutral';
};

const arrivalConditionTone = (val) => {
  if (val === 'great') return 'positive';
  if (val === 'small_issues') return 'warning';
  if (val === 'missing_or_incorrect') return 'danger';
  return 'neutral';
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
      {title}
    </h3>
    <div>{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-[12px] text-zinc-500">{label}</span>
    <span className="text-[12px] font-medium text-zinc-800 text-right">{value || '—'}</span>
  </div>
);

const GuestFeedbackDrawer = ({ feedback, onClose }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!feedback) return null;

  const order = feedback.order || {};
  const handleCopy = async () => {
    if (!feedback.openComment) return;
    try {
      await navigator.clipboard.writeText(feedback.openComment);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* swallow — clipboard may be blocked in some contexts */
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-zinc-900/40"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      />
      <aside className="relative h-full w-full max-w-[520px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-zinc-100 bg-white/95 px-6 py-4 backdrop-blur">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
              Guest Feedback
            </p>
            <h2 className="mt-0.5 truncate text-[16px] font-bold text-zinc-900">
              Order #{feedback.orderUniqueId}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
          >
            <FiX className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-6 p-6">
          {/* Stars + meta */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  Overall
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <StarRow value={feedback.overallStars} />
                  <span className="text-[13px] font-semibold text-zinc-900">
                    {feedback.overallStars}/5
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  Submitted
                </p>
                <p className="mt-1 text-[12px] font-medium text-zinc-700">
                  {formatDate(feedback.submittedAt || feedback.createdAt)}
                </p>
              </div>
            </div>
          </div>

          <Section title="Survey Answers">
            <div className="space-y-2">
              <div className="rounded-lg border border-zinc-100 bg-white p-3">
                <p className="text-[11px] text-zinc-500">Arrival experience</p>
                <div className="mt-1">
                  {feedback.arrivalExperience ? (
                    <Badge tone={arrivalExperienceTone(feedback.arrivalExperience)}>
                      {ARRIVAL_EXPERIENCE_LABELS[feedback.arrivalExperience] ||
                        feedback.arrivalExperience}
                    </Badge>
                  ) : (
                    <span className="text-[12px] text-zinc-400">Skipped</span>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-100 bg-white p-3">
                <p className="text-[11px] text-zinc-500">Order condition</p>
                <div className="mt-1">
                  {feedback.arrivalCondition ? (
                    <Badge tone={arrivalConditionTone(feedback.arrivalCondition)}>
                      {ARRIVAL_CONDITION_LABELS[feedback.arrivalCondition] ||
                        feedback.arrivalCondition}
                    </Badge>
                  ) : (
                    <span className="text-[12px] text-zinc-400">Skipped</span>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-100 bg-white p-3">
                <p className="text-[11px] text-zinc-500">Biggest difference</p>
                <div className="mt-1">
                  {feedback.biggestDifference ? (
                    <Badge tone="neutral">
                      {BIGGEST_DIFFERENCE_LABELS[feedback.biggestDifference] ||
                        feedback.biggestDifference}
                    </Badge>
                  ) : (
                    <span className="text-[12px] text-zinc-400">Skipped</span>
                  )}
                </div>
              </div>
            </div>
          </Section>

          <Section title="In Their Words">
            {feedback.openComment ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-4">
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-800">
                  &ldquo;{feedback.openComment}&rdquo;
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-medium text-zinc-700 hover:bg-zinc-50"
                >
                  {copied ? (
                    <>
                      <FiCheck className="h-3.5 w-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <FiCopy className="h-3.5 w-3.5" /> Copy quote
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 p-4 text-[12px] text-zinc-400">
                No written comment.
              </div>
            )}
          </Section>

          <Section title="Order">
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <Row label="Unique ID" value={order.uniqueId || feedback.orderUniqueId} />
              <Row label="Property" value={order.propertyTitle} />
              <Row
                label="Location"
                value={order.city ? `${order.city}${order.state ? `, ${order.state}` : ''}` : ''}
              />
              <Row label="Guest email" value={order.guestEmail} />
              <Row label="Status" value={order.status} />
              <Row label="Placed" value={formatDate(order.placedAt || order.createdAt)} />
              {order.id ? (
                <div className="mt-3 border-t border-zinc-100 pt-3">
                  <Link
                    to={`/orderList/${order.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Open order detail <FiExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ) : null}
            </div>
          </Section>
        </div>
      </aside>
    </div>
  );
};

export default GuestFeedbackDrawer;
